import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  ArrowBack,
  Visibility,
  Delete,
  Save,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const ExamCreator = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [examData, setExamData] = useState({
    title: '',
    description: '',
    area: '',
    time_limit: 60,
    passing_score: 60,
  });
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [filters, setFilters] = useState({
    area: '',
    topic: '',
    difficulty: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewQuestion, setPreviewQuestion] = useState(null);

  const steps = ['Información del Examen', 'Seleccionar Preguntas', 'Revisar y Crear'];

  useEffect(() => {
    if (activeStep === 1) {
      loadQuestions();
    }
  }, [activeStep, filters]);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        status: 'validated', // Solo preguntas validadas
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== '')
        ),
      });

      const response = await axios.get(`${API_URL}/api/questions?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setAvailableQuestions(response.data.questions);
    } catch (err) {
      console.error('Error loading questions:', err);
      setError('Error al cargar las preguntas');
    } finally {
      setLoading(false);
    }
  };

  const handleExamDataChange = (e) => {
    const { name, value } = e.target;
    setExamData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuestionToggle = (questionId) => {
    setSelectedQuestions((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    );
  };

  const handleNext = () => {
    if (activeStep === 0) {
      // Validar datos del examen
      if (!examData.title || !examData.area) {
        setError('Por favor completa todos los campos requeridos');
        return;
      }
    } else if (activeStep === 1) {
      // Validar que haya preguntas seleccionadas
      if (selectedQuestions.length === 0) {
        setError('Debes seleccionar al menos una pregunta');
        return;
      }
    }
    setError('');
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleCreateExam = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const examPayload = {
        ...examData,
        question_ids: selectedQuestions,
      };

      await axios.post(`${API_URL}/api/specific-exams`, examPayload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert('¡Examen creado exitosamente!');
      navigate('/teacher/exams');
    } catch (err) {
      console.error('Error creating exam:', err);
      setError(err.response?.data?.message || 'Error al crear el examen');
    } finally {
      setLoading(false);
    }
  };

  const getAreaLabel = (area) => {
    const labels = {
      matematicas: 'Matemáticas',
      'lectura-critica': 'Lectura Crítica',
      sociales: 'Sociales y Ciudadanas',
      ciencias: 'Ciencias',
      ingles: 'Inglés',
    };
    return labels[area] || area;
  };

  const getDifficultyLabel = (difficulty) => {
    const labels = {
      easy: 'Fácil',
      medium: 'Medio',
      hard: 'Difícil',
    };
    return labels[difficulty] || difficulty;
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Información Básica del Examen
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Título del Examen"
                  name="title"
                  value={examData.title}
                  onChange={handleExamDataChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descripción"
                  name="description"
                  value={examData.description}
                  onChange={handleExamDataChange}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Área</InputLabel>
                  <Select
                    name="area"
                    value={examData.area}
                    onChange={handleExamDataChange}
                    label="Área"
                  >
                    <MenuItem value="matematicas">Matemáticas</MenuItem>
                    <MenuItem value="lectura-critica">Lectura Crítica</MenuItem>
                    <MenuItem value="sociales">Sociales</MenuItem>
                    <MenuItem value="ciencias">Ciencias</MenuItem>
                    <MenuItem value="ingles">Inglés</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Tiempo Límite (minutos)"
                  name="time_limit"
                  type="number"
                  value={examData.time_limit}
                  onChange={handleExamDataChange}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Puntaje Mínimo (%)"
                  name="passing_score"
                  type="number"
                  value={examData.passing_score}
                  onChange={handleExamDataChange}
                  inputProps={{ min: 0, max: 100 }}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Seleccionar Preguntas ({selectedQuestions.length} seleccionadas)
            </Typography>

            {/* Filtros */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Área</InputLabel>
                  <Select
                    name="area"
                    value={filters.area}
                    onChange={handleFilterChange}
                    label="Área"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    <MenuItem value="matematicas">Matemáticas</MenuItem>
                    <MenuItem value="lectura-critica">Lectura Crítica</MenuItem>
                    <MenuItem value="sociales">Sociales</MenuItem>
                    <MenuItem value="ciencias">Ciencias</MenuItem>
                    <MenuItem value="ingles">Inglés</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tema"
                  name="topic"
                  value={filters.topic}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Dificultad</InputLabel>
                  <Select
                    name="difficulty"
                    value={filters.difficulty}
                    onChange={handleFilterChange}
                    label="Dificultad"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    <MenuItem value="easy">Fácil</MenuItem>
                    <MenuItem value="medium">Medio</MenuItem>
                    <MenuItem value="hard">Difícil</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Tabla de Preguntas */}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">Sel.</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>Pregunta</TableCell>
                    <TableCell>Área</TableCell>
                    <TableCell>Dificultad</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {availableQuestions.map((question) => (
                    <TableRow key={question.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedQuestions.includes(question.id)}
                          onChange={() => handleQuestionToggle(question.id)}
                        />
                      </TableCell>
                      <TableCell>{question.id}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {question.question}
                        </Typography>
                      </TableCell>
                      <TableCell>{getAreaLabel(question.area)}</TableCell>
                      <TableCell>
                        <Chip
                          label={getDifficultyLabel(question.difficulty)}
                          size="small"
                          color={
                            question.difficulty === 'easy'
                              ? 'success'
                              : question.difficulty === 'medium'
                              ? 'info'
                              : 'error'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => setPreviewQuestion(question)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );

      case 2:
        const selectedQuestionsData = availableQuestions.filter((q) =>
          selectedQuestions.includes(q.id)
        );

        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Revisar Examen
            </Typography>

            {/* Información del Examen */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Información del Examen
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <strong>Título:</strong> {examData.title}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <strong>Área:</strong> {getAreaLabel(examData.area)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <strong>Tiempo Límite:</strong> {examData.time_limit} minutos
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <strong>Puntaje Mínimo:</strong> {examData.passing_score}%
                  </Typography>
                </Grid>
                {examData.description && (
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>Descripción:</strong> {examData.description}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Preguntas Seleccionadas */}
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Preguntas Seleccionadas ({selectedQuestionsData.length})
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Pregunta</TableCell>
                    <TableCell>Área</TableCell>
                    <TableCell>Dificultad</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedQuestionsData.map((question) => (
                    <TableRow key={question.id}>
                      <TableCell>{question.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                          {question.question}
                        </Typography>
                      </TableCell>
                      <TableCell>{getAreaLabel(question.area)}</TableCell>
                      <TableCell>
                        <Chip
                          label={getDifficultyLabel(question.difficulty)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            setSelectedQuestions((prev) =>
                              prev.filter((id) => id !== question.id)
                            )
                          }
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/teacher')}
          variant="outlined"
        >
          Volver
        </Button>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          Crear Examen
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Step Content */}
        {renderStepContent()}

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button disabled={activeStep === 0} onClick={handleBack}>
            Atrás
          </Button>
          <Box>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleCreateExam}
                disabled={loading}
                startIcon={<Save />}
              >
                {loading ? 'Creando...' : 'Crear Examen'}
              </Button>
            ) : (
              <Button variant="contained" onClick={handleNext}>
                Siguiente
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Preview Dialog */}
      <Dialog
        open={Boolean(previewQuestion)}
        onClose={() => setPreviewQuestion(null)}
        maxWidth="md"
        fullWidth
      >
        {previewQuestion && (
          <>
            <DialogTitle>Vista Previa de Pregunta</DialogTitle>
            <DialogContent>
              <Typography variant="h6" gutterBottom>
                {previewQuestion.question}
              </Typography>
              <Box sx={{ mb: 2 }}>
                {previewQuestion.options.map((option, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 1,
                      mb: 1,
                      border: 1,
                      borderColor:
                        String.fromCharCode(65 + index) === previewQuestion.correct_answer
                          ? 'success.main'
                          : 'grey.300',
                      borderRadius: 1,
                      bgcolor:
                        String.fromCharCode(65 + index) === previewQuestion.correct_answer
                          ? 'success.50'
                          : 'transparent',
                    }}
                  >
                    <Typography variant="body2">
                      <strong>{String.fromCharCode(65 + index)}.</strong> {option}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPreviewQuestion(null)}>Cerrar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default ExamCreator;
